import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  TGatewayHealthEntry,
  TGatewaysHealthActions,
  TGatewaysHealthData,
} from '@/console/features/gateways/hooks/use-gateway-health/gateways-health-context.types';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadGatewaysHealthStatusQuery } from '@/shared/redux/api/assets/gateways/gateways-api';

export const GATEWAYS_HEALTH_POLLING_INTERVAL_MS = 60_000;

const GatewaysHealthActionsContext =
  createContext<TGatewaysHealthActions | null>(null);

const GatewaysHealthDataContext = createContext<TGatewaysHealthData | null>(
  null,
);

export const useGatewaysHealthActions = (): TGatewaysHealthActions | null =>
  useContext(GatewaysHealthActionsContext);

export const useGatewaysHealthData = (): TGatewaysHealthData | null =>
  useContext(GatewaysHealthDataContext);

export const GatewaysHealthProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const refCounts = useRef<Map<string, number>>(new Map());
  const [ids, setIds] = useState<string[]>([]);

  const recomputeIds = useCallback(() => {
    setIds(Array.from(refCounts.current.keys()).sort());
  }, []);

  const register = useCallback(
    (gatewayId: string) => {
      const next = (refCounts.current.get(gatewayId) ?? 0) + 1;
      refCounts.current.set(gatewayId, next);
      if (next === 1) {
        recomputeIds();
      }
    },
    [recomputeIds],
  );

  const unregister = useCallback(
    (gatewayId: string) => {
      const current = refCounts.current.get(gatewayId) ?? 0;
      if (current <= 1) {
        refCounts.current.delete(gatewayId);
        recomputeIds();
      } else {
        refCounts.current.set(gatewayId, current - 1);
      }
    },
    [recomputeIds],
  );

  const actions = useMemo<TGatewaysHealthActions>(
    () => ({ register, unregister }),
    [register, unregister],
  );

  const { currentData } = usePollingQuery({
    useQueryHook: useReadGatewaysHealthStatusQuery,
    queryArg: { gatewayIds: ids },
    pollingInterval: GATEWAYS_HEALTH_POLLING_INTERVAL_MS,
    queryOptions: { skip: ids.length === 0 },
  });

  const dataValue = useMemo<TGatewaysHealthData>(() => {
    const states: Record<string, TGatewayHealthEntry> = {};

    // currentData, never data: rows register and unregister their id as they
    // mount, and `data` keeps serving the previous id set's result — which would
    // hand a freshly mounted row another gateway set's health as an answer.
    currentData?.readGatewaysByIds?.items?.forEach((item) => {
      if (item.gatewayId) {
        states[item.gatewayId] = {
          state: item.state?.state,
          reportedAt: item.state?.reportedAt,
        };
      }
    });

    // What this response covers. A gateway outside it is unknown rather than
    // "no health reported", which is the difference between loading and a state.
    const answeredIds = new Set(currentData ? ids : []);

    return {
      states,
      isPendingFor: (gatewayId: string) => !answeredIds.has(gatewayId),
    };
  }, [currentData, ids]);

  return (
    <GatewaysHealthActionsContext.Provider value={actions}>
      <GatewaysHealthDataContext.Provider value={dataValue}>
        {children}
      </GatewaysHealthDataContext.Provider>
    </GatewaysHealthActionsContext.Provider>
  );
};
